import { getCurrentSizeProfile } from "@common/components/Typography/style";
import {
  Backdrop,
  CheckboxProps,
  CircularProgress,
  Divider,
  Pagination,
  Stack,
  styled,
  SxProps,
} from "@mui/material";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { BodyRow, HeaderRow } from "./ListCell";
import { getListControls } from "./getListControls";
import DragLayer from "@common/components/DragNDrop/DragLayer";
import DndProvider from "@common/components/DragNDrop";
import { ListDnDTypes } from "./const";
import _ from "lodash";

export const Container = styled("div")(() => ({
  position: "relative",
  maxHeight: "100%",
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));
export const Table = styled("table")<{ disableColumnResize?: boolean }>(
  ({ disableColumnResize }) => ({
    maxHeight: "100%",
    borderSpacing: 0,
    tableLayout: "fixed",
    ...(disableColumnResize
      ? {
          width: "100%",
          "& th": {
            width: "auto!important",
            minWidth: "auto!important",
          },
        }
      : {}),
  })
);

export const TableHead = styled("thead")(() => ({
  position: "sticky",
  top: 0,
  zIndex: 3,
}));
export const TableBody = styled("tbody")(() => ({
  zIndex: 2,
}));

export const StyledStack = styled(Stack)({
  flex: 1,
  overflow: "auto",
  width: "100%",
  alignItems: "flex-start",
});
const DragColumnLayer = ({
  item,
}: {
  // FIXME: get rid of any
  item?: any;
  itemType?: any;
}) => (
  <Stack
    sx={{
      bgcolor: ({ palette }) => `${palette.background.paper}BB`,
      height: "48px",
      width: item?.col?.width ?? "16rem",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {item?.col?.label}
  </Stack>
);

/**
 * @param ref pass a ref to this component to have access to the controls it exposes.
 * The `ref.current`'s value will be shaped as indicated by the type `ListControls<T>`
 */
const ListPreview = <T, K extends keyof T, O = undefined>(
  props: ListViewProps<T, K, O> & { sx?: SxProps; testId?: string },
  ref: React.ForwardedRef<ListControls<T>>
) => {
  const {
    columns,
    onColumnsOrderChange,
    onColumnWidthChange,
    data,
    identityKey,
    defaultWidth = 260,
    defaultMinWidth = 64,
    rowHeight = `${parseFloat(getCurrentSizeProfile().small) * 2.5}em`,
    loading,
    pinFirstColumn = false,
    total = 0,
    pageSize = 30,
    page = 1,
    selectable = false,
    customSelector,
    isRowSelectable = () => true,
    isRowHighlighted = () => false,
    setPage = () => {},
    onSelect = () => {},
    sort,
    onSort = () => {},
    sx,
    selectionBehavior,
    disabledCheckbox,
    defaultSelected,
    notSelectableReason,
    disableColumnResize = false,
    rememberScrollTop = false,
    testId,
  } = props;

  const scrollYRef = useRef<number | undefined>(undefined);

  const tableRef = useRef<HTMLTableElement>(null);
  const containerRef = useRef<HTMLTableRowElement>(null);
  const listControls = getListControls({ ...props, tableRef });
  useImperativeHandle(ref, () => listControls);

  const [selected, handleSelection] = useReducer(
    (
      state: T[K][] | undefined,
      {
        selected,
        propagateChanges = true,
      }: { selected: T[K] | T[K][]; propagateChanges?: boolean }
    ): T[K][] | undefined => {
      let newValue = [];

      if (Array.isArray(selected)) newValue = [...selected];
      else if (state === undefined || selectionBehavior === "radio")
        newValue = [selected];
      else if (state.includes(selected))
        newValue = [...state.filter((s) => s !== selected)];
      else newValue = [...state, selected];

      if (propagateChanges) onSelect(newValue);
      return newValue;
    },
    undefined,
    () => props.selected ?? defaultSelected
  );

  useEffect(() => {
    if (props.selected !== undefined && !_.isEqual(props.selected, selected)) {
      handleSelection({ selected: props.selected, propagateChanges: false });
    }
  }, [props.selected]);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
    setPage(value);
  };

  useEffect(() => {
    if (rememberScrollTop && scrollYRef.current) {
      listControls.scrollToPosition({ top: scrollYRef.current });
    }

    if (selected !== undefined && selected.length !== 0) {
      const newIds = data.map((x) => x[(identityKey ?? "id") as keyof T]);
      if (!_.isEqual(newIds, selected)) {
        handleSelection({
          selected:
            defaultSelected ??
            selected.filter((select) => newIds.includes(select)),
          propagateChanges: props.updateSelection ?? true,
        });
      }
    }
  }, [JSON.stringify(data.map((x) => x[(identityKey ?? "id") as keyof T]))]);

  useEffect(() => {
    if (defaultSelected) {
      handleSelection({
        selected: defaultSelected ?? [],
        propagateChanges: props.updateSelection ?? true,
      });
    }
  }, [disabledCheckbox]);

  const dataSelectable = React.useMemo(
    () =>
      data.filter((x) => isRowSelectable === undefined || isRowSelectable(x)),
    [JSON.stringify(data), isRowSelectable]
  );

  const handleCheckboxChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) =>
      handleSelection({
        selected:
          checked && identityKey !== undefined
            ? dataSelectable.map((d) => d[identityKey])
            : [],
      }),
    [identityKey, dataSelectable]
  );
  const headerCheckboxProps: CheckboxProps = {
    indeterminate:
      (selected?.length ?? 0) > 0 &&
      dataSelectable.length > 0 &&
      selected?.length !== dataSelectable.length,
    checked:
      (selected?.length ?? 0) > 0 &&
      dataSelectable.length > 0 &&
      selected?.length === dataSelectable.length,
    onChange: handleCheckboxChange,
  };
  const showColumns = useMemo(
    () => columns.filter((c) => c.show !== false),
    [columns]
  );

  useEffect(() => {
    const parentElement = tableRef.current?.parentElement;

    if (rememberScrollTop && parentElement) {
      const handleScroll = () => {
        if (data.length !== 0) {
          scrollYRef.current = tableRef.current?.parentElement?.scrollTop;
        }
      };

      parentElement.addEventListener("scroll", handleScroll);

      return () => {
        parentElement.removeEventListener("scroll", handleScroll);
      };
    }

    return undefined;
  }, [data]);

  useEffect(() => {
    if (rememberScrollTop) {
      scrollYRef.current = 0;
    }
  }, [page]);

  return (
    <DndProvider>
      <Container sx={sx} data-testid={testId}>
        {loading && (
          <Backdrop open sx={{ position: "absolute", zIndex: 100 }}>
            <CircularProgress />
          </Backdrop>
        )}

        <Divider orientation="horizontal" flexItem />

        <StyledStack ref={containerRef}>
          <DragLayer
            component={[
              {
                itemType: ListDnDTypes.LIST_ORDER,
                component: <DragColumnLayer />,
              },
            ]}
          />

          <Table ref={tableRef} disableColumnResize={disableColumnResize}>
            <TableHead>
              <HeaderRow
                {...{
                  columns: showColumns,
                  onColumnsOrderChange,
                  onColumnWidthChange,
                  rowHeight,
                  defaultWidth,
                  defaultMinWidth,
                  pinFirstColumn,
                  selectable,
                  selected,
                  CheckboxProps: headerCheckboxProps,
                  sort,
                  onSort,
                  disabledCheckbox:
                    disabledCheckbox || dataSelectable.length === 0,
                  selectionBehavior,
                  disableColumnResize,
                }}
                containerRef={containerRef}
              />
            </TableHead>

            <TableBody>
              {data.map((row, index) => (
                <BodyRow
                  key={
                    identityKey !== undefined ? `${row[identityKey]}` : index
                  }
                  {...{
                    columns: showColumns,
                    rowHeight,
                    row,
                    pinFirstColumn,
                    selectable,
                    isRowSelectable,
                    isRowHighlighted,
                    disabledCheckbox,
                    selectionBehavior,
                    customSelector,
                    checked:
                      identityKey !== undefined &&
                      selected?.includes(row[identityKey]),
                    onSelect: () => {
                      if (identityKey !== undefined)
                        handleSelection({ selected: row[identityKey] });
                    },
                    listControls,
                    notSelectableReason,
                  }}
                />
              ))}
            </TableBody>
          </Table>
        </StyledStack>

        {Math.ceil(total / pageSize) > 1 && (
          <Pagination
            shape="rounded"
            count={Math.ceil(total / pageSize)}
            onChange={handleChange}
            page={page}
            sx={{
              marginTop: 1,
              justifyContent: "center",
              display: "flex",
            }}
          />
        )}
      </Container>
    </DndProvider>
  );
};

// forwardRef tends to eat the generics and replace all of them by unknown/never
// when passing down components with generics.
// Need to use type assertion to inform typescript of what's happening
const ForwardingList = React.forwardRef(ListPreview) as unknown as <
  T,
  K extends keyof T,
  O = undefined
>(
  props: ListViewProps<T, K, O> & {
    sx?: SxProps;
    testId?: string;
    ref?: React.ForwardedRef<ListControls<T>>;
  }
) => ReturnType<typeof ListPreview>;

// Needed to use generics through React.memo
const typedMemo: <T>(c: T) => T = React.memo;
export default typedMemo(ForwardingList);
